import {
  Button,
  type ButtonPriority,
  ButtonRow,
  type ButtonSize,
  LinkButton,
  PopoverConfirm,
} from '@timelyapp/react';
import classNames from 'classnames';
import { ModalHeader, ModalHeaderGroup } from 'components/modals/index';
import React from 'react';
import styles from './ConfirmationDialogue.module.scss';
import ModalContent from './ModalContent';
import ModalFooter from './ModalFooter';
import type { Props as SuperModalProps } from './SuperModal';
import SuperModal from './SuperModal';

interface ConfirmationButtonType {
  text: string;
  priority?: ButtonPriority;
  destructive?: boolean;
  size?: ButtonSize;
  onClick?: () => void;
  confirm?: boolean;
  confirmText?: string;
  confirmLabel?: string;
  fullWidth?: boolean;
  linkTo?: string;
  hasIcon?: boolean;
  icon?: React.ReactElement;
  isLoading?: boolean;
  disabled?: boolean;
}
export type ConfirmationButtonTypes = Array<ConfirmationButtonType>;

interface InternalProps {
  /** Fires on button click and modal close */
  onClose?: () => void;
  children: React.ReactNode;
  /** Dialog's title */
  title: string;
  /** Width of dialogue */
  width?: number;
  /** Keep header/footer visible and scroll only the body on small viewports. */
  constrainToViewport?: boolean;
  /** Action buttons in dialogue (they fire SuperModal's onClose on click) */
  buttons?: ConfirmationButtonTypes;
}

interface Props extends SuperModalProps, InternalProps {}

const ConfirmationDialogue = (props: Props) => {
  const { children, title, buttons, width = 300, constrainToViewport = false, ...rest } = props;
  return (
    <SuperModal
      {...rest}
      overlayClassName={
        constrainToViewport
          ? classNames(props.overlayClassName, styles.overlay)
          : props.overlayClassName
      }
      surfaceClassName={classNames(props.surfaceClassName, constrainToViewport && styles.surface)}
      style={{
        content: { width, ...(constrainToViewport ? { maxWidth: 'calc(100vw - 48px)' } : {}) },
      }}
    >
      <ModalHeader withForm onClose={props.onClose}>
        <ModalHeaderGroup title>{title}</ModalHeaderGroup>
      </ModalHeader>
      <ModalContent withForm className={constrainToViewport ? styles.body : undefined}>
        {children}
      </ModalContent>
      {!!buttons?.length && (
        <ModalFooter withForm>
          <ButtonRow style={{ flex: '1' }}>
            {buttons.map(button => {
              if (button.confirm) {
                return (
                  <PopoverConfirm
                    key={button.text}
                    confirmLabel={button.confirmLabel || 'Confirm'}
                    onConfirm={() => {
                      button.onClick?.();
                    }}
                    confirmButtonPriority={button.priority}
                    prompt={button.confirmText}
                    target={
                      <Button
                        key={button.text}
                        priority={button.priority || 'high'}
                        size={button.size}
                        style={{ flex: button.fullWidth ? '1' : undefined, alignItems: 'center' }}
                      >
                        {button.icon && <div style={{ marginRight: 5 }}>{button.icon}</div>}
                        {button.text}
                      </Button>
                    }
                    targetCaret={false}
                  />
                );
              }
              if (button.linkTo) {
                return (
                  <LinkButton
                    key={button.text}
                    priority={button.priority || 'high'}
                    size={button.size}
                    linkTo={button.linkTo}
                    isLoading={button.isLoading}
                    disabled={button.disabled}
                    onClick={() => {
                      button.onClick?.();
                    }}
                    style={{ flex: button.fullWidth ? '1' : undefined }}
                  >
                    {button.icon && <span>{button.icon}</span>}
                    <span>{button.text}</span>
                  </LinkButton>
                );
              }
              return (
                <Button
                  key={button.text}
                  priority={button.priority || 'high'}
                  size={button.size}
                  isLoading={button.isLoading}
                  disabled={button.disabled}
                  destructive={button.destructive}
                  onClick={() => {
                    button.onClick?.();
                  }}
                  style={{ flex: button.fullWidth ? '1' : undefined, alignItems: 'center' }}
                >
                  {button.icon && <span>{button.icon}</span>}
                  <span>{button.text}</span>
                </Button>
              );
            })}
          </ButtonRow>
        </ModalFooter>
      )}
    </SuperModal>
  );
};

export default ConfirmationDialogue;
